import { gql, useMutation, useQuery } from '@apollo/client'
import type { MutationHookOptions, QueryHookOptions } from '@apollo/client'

/**
 * Feature-access GraphQL operations (coupon-unlocked features).
 *
 * These are hand-written, fully-typed Apollo hooks that mirror the `.graphql`
 * documents in src/graphql (kept as the codegen source of truth). They live here
 * so the coupon-unlock flow is strongly typed and compiles without requiring a
 * live codegen run against the backend — matching the registration operations.
 */

/** A gated feature that can be temporarily unlocked with a coupon. */
export type UnlockableFeature = 'analytics'

export type FeatureUnlockPayload = {
  feature?: UnlockableFeature | string | null
  /** ISO timestamp when the unlock ends. */
  expiresAt?: string | null
  active?: boolean | null
}

// ---------------------------------------------------------------------------
// getMyFeatureUnlocks — active feature unlocks for the current company
// ---------------------------------------------------------------------------

export type GetMyFeatureUnlocksQuery = {
  getMyFeatureUnlocks?: (FeatureUnlockPayload | null)[] | null
}

export const GET_MY_FEATURE_UNLOCKS = gql`
  query getMyFeatureUnlocks {
    getMyFeatureUnlocks {
      feature
      expiresAt
      active
    }
  }
`

export const useGetMyFeatureUnlocksQuery = (
  options?: QueryHookOptions<GetMyFeatureUnlocksQuery, Record<string, never>>
) => useQuery<GetMyFeatureUnlocksQuery, Record<string, never>>(GET_MY_FEATURE_UNLOCKS, options)

// ---------------------------------------------------------------------------
// redeemFeatureCoupon — redeem a coupon to unlock a feature for a period
// ---------------------------------------------------------------------------

export type RedeemFeatureCouponMutation = {
  redeemFeatureCoupon?: FeatureUnlockPayload | null
}

export type RedeemFeatureCouponMutationVariables = {
  code: string
}

export const REDEEM_FEATURE_COUPON = gql`
  mutation redeemFeatureCoupon($code: String!) {
    redeemFeatureCoupon(code: $code) {
      feature
      expiresAt
      active
    }
  }
`

export const useRedeemFeatureCouponMutation = (
  options?: MutationHookOptions<RedeemFeatureCouponMutation, RedeemFeatureCouponMutationVariables>
) =>
  useMutation<RedeemFeatureCouponMutation, RedeemFeatureCouponMutationVariables>(
    REDEEM_FEATURE_COUPON,
    options
  )

// ---------------------------------------------------------------------------
// getMyFeatureAccess — the server's entitlement verdict for a gated feature
// ---------------------------------------------------------------------------

/** Why access is (or is not) granted. */
export type FeatureAccessSource = 'coupon' | 'manual_grace' | 'plan' | 'none'

export type FeatureAccessPayload = {
  feature?: string | null
  hasAccess?: boolean | null
  source?: FeatureAccessSource | string | null
  /** When a time-limited grant ends (coupon expiry, or the manual-plan cutoff). */
  accessEndsAt?: string | null
  /** Whether to run the manual-plan countdown. False for coupon holders. */
  showCountdown?: boolean | null
  /** Whether to prompt this account to move onto an automated plan. */
  requiresMigration?: boolean | null
  migrationDeadline?: string | null
  isManual?: boolean | null
}

export type GetMyFeatureAccessQuery = {
  getMyFeatureAccess?: FeatureAccessPayload | null
}

export type GetMyFeatureAccessQueryVariables = {
  feature?: string | null
}

export const GET_MY_FEATURE_ACCESS = gql`
  query getMyFeatureAccess($feature: String) {
    getMyFeatureAccess(feature: $feature) {
      feature
      hasAccess
      source
      accessEndsAt
      showCountdown
      requiresMigration
      migrationDeadline
      isManual
    }
  }
`

export const useGetMyFeatureAccessQuery = (
  options?: QueryHookOptions<GetMyFeatureAccessQuery, GetMyFeatureAccessQueryVariables>
) =>
  useQuery<GetMyFeatureAccessQuery, GetMyFeatureAccessQueryVariables>(
    GET_MY_FEATURE_ACCESS,
    options
  )

// ---------------------------------------------------------------------------
// migrateToAutomatedPlan — move a manual account onto a Stripe-billed plan
// ---------------------------------------------------------------------------

export type PlanMigrationPayload = {
  status?: string | null
  planId?: string | null
  planName?: string | null
  priceId?: string | null
  currency?: string | null
  /** When the first automatic charge falls due. */
  billingStartsAt?: string | null
}

export type MigrateToAutomatedPlanMutation = {
  migrateToAutomatedPlan?: PlanMigrationPayload | null
}

export type MigrateToAutomatedPlanMutationVariables = {
  input: {
    productPlanId: string
    paymentMethodId: string
    /** Lowercase ISO currency ('gbp' | 'usd' | 'zar'). */
    currency?: string | null
  }
}

export const MIGRATE_TO_AUTOMATED_PLAN = gql`
  mutation migrateToAutomatedPlan($input: MigrateToAutomatedPlanInput) {
    migrateToAutomatedPlan(input: $input) {
      status
      planId
      planName
      priceId
      currency
      billingStartsAt
    }
  }
`

export const useMigrateToAutomatedPlanMutation = (
  options?: MutationHookOptions<
    MigrateToAutomatedPlanMutation,
    MigrateToAutomatedPlanMutationVariables
  >
) =>
  useMutation<MigrateToAutomatedPlanMutation, MigrateToAutomatedPlanMutationVariables>(
    MIGRATE_TO_AUTOMATED_PLAN,
    options
  )
